import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
	ActionDecorations,
	ActionFeedback,
	ActionInput,
	ActionMedia,
	ActionNotation,
	ActionPrivacy,
	ActionSubmit,
	ActionTags,
	ActionText,
} from '@bonusly/common-action-input';
import { Box, Button, Flex, Hide, ModalBody, ModalFooter, rem, Spacer, Stack, TrashOutline } from '@bonusly/design-system';

import { Bonus } from '../../../../../types/bonus';
import { useEditModal } from '../../hooks/use-edit-modal';
import { useInputFocus } from '../../hooks/use-input-focus';

interface EditModalContentProps {
	bonus: Bonus;
	onClose: VoidFunction;
	onDelete: VoidFunction;
	onRequestClose: VoidFunction;
	onSuccess: VoidFunction;
}

export interface EditModalHandlers {
	requestClose: VoidFunction;
}

export const EditModalContent = forwardRef<EditModalHandlers, EditModalContentProps>(({ bonus, onClose, onDelete, onRequestClose, onSuccess }, ref) => {
	const { t } = useTranslation('common');
	const { isFocused, ...handlers } = useInputFocus();
	const { config, handleClose } = useEditModal({
		bonus,
		onClose,
		onRequestClose,
		onSuccess,
		ref,
	});

	// The ActionInput component uses its own I18nProvider, so we need to use the
	// useTranslation hook from this component's context to get the correct
	// translations. Once all MFEs and widgets share the same I18nProvider, we
	// can call t() within the scope of ActionInput.
	const submitText = t('save_changes');
	const deleteText = t('delete');
	const cancelText = t('cancel');

	return (
		<ActionInput autofocus="end" {...config}>
			<ModalBody {...handlers}>
				<Flex alignItems="flex-start" justifyContent="space-between">
					<ActionTags />
					<Hide below="xs">
						<ActionPrivacy />
					</Hide>
				</Flex>

				<Box mb={2}>
					<ActionText inputStyles={{ minHeight: rem(150) }} />
					<ActionNotation isVisible={isFocused} />
					<ActionFeedback />
				</Box>

				<ActionMedia w="full" />
			</ModalBody>

			<ModalFooter>
				<Stack w="full">
					<Flex justifyContent="space-between" flexGrow={1}>
						<ActionDecorations />
						<Hide above="xs">
							<ActionPrivacy />
						</Hide>
					</Flex>

					<Flex
						alignItems={{ base: 'stretch', xs: 'flex-start' }}
						direction={{ base: 'column', xs: 'row' }}
						gap={2}
						justifyContent="space-between"
						mb={2}
						w="full"
					>
						<Button colorScheme="secondary" variant="alpha" size="lg" leftIcon={<TrashOutline />} onClick={onDelete}>
							{deleteText}
						</Button>
						<Spacer />

						<Hide below="xs">
							<Button variant="ghost" mr={3} onClick={handleClose} size="lg">
								{cancelText}
							</Button>
						</Hide>
						<ActionSubmit>{submitText}</ActionSubmit>
					</Flex>
				</Stack>
			</ModalFooter>
		</ActionInput>
	);
});

EditModalContent.displayName = 'EditModalContent';
